import React, { useEffect, useState } from "react";
import { AlertCircle, Loader2, ArrowRight, ExternalLink } from "lucide-react";
import { classifySuspension, TIER_INFO } from "../lib/triageClassifier";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Embedded (iframe-safe) version of the Free Suspension Analyzer.
 *
 * Renders WITHOUT the site chrome (Header/Footer/CookieConsent). Publishers
 * paste an <iframe> pointing here on their own site. Every page view also
 * fires POST /api/embed/ping so we can log the referring domain for our
 * Backlink Command Center dashboard.
 *
 * Attribution footer contains a dofollow link back to the primary site,
 * which is the whole point — this is the linkable asset.
 */
export default function EmbedAnalyzerPage() {
  const [platform, setPlatform] = useState("");
  const [accountType, setAccountType] = useState("");
  const [reasonText, setReasonText] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Fire embed-referrer telemetry so we know where our widget lives.
    try {
      fetch(`${API}/embed/ping`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          referrer: document.referrer || "",
          embedVersion: "1.0",
        }),
      }).catch(() => {});
    } catch (_e) {
      // Silent — never block the widget on telemetry failure.
    }
  }, []);

  const handleAnalyze = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Client-side classification (mirrors /analyze page)
    const classification = classifySuspension({
      noticeText: reasonText,
      suspensionType: "unknown",
      accountType,
    });
    setResult(classification);
    // Fire-and-forget backend record — no PII on the embed.
    try {
      await fetch(`${API}/triage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          platform,
          accountType,
          suspensionType: "unknown",
          suspensionNoticeText: reasonText,
          classifiedCategory: classification.id,
          classifiedSeverity: classification.severity,
          recommendedTier: classification.recommendedTier,
        }),
      });
    } catch (_e) {
      // Never block user on backend failure.
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b] p-4 md:p-6" data-testid="embed-analyzer">
      <div className="max-w-2xl mx-auto">
        {/* Compact header */}
        <div className="mb-5">
          <p className="text-[10px] uppercase tracking-widest text-[#d9fb06] font-mono mb-1">
            Free · 60 seconds · No signup
          </p>
          <h1 className="text-xl md:text-2xl font-black text-[#dfddd6] leading-tight">
            Suspension Analyzer
          </h1>
          <p className="text-xs text-[#888680] mt-1">
            Paste your suspension notice and identify the most likely policy category.
          </p>
        </div>

        {!result ? (
          <form onSubmit={handleAnalyze} className="space-y-4 bg-[#252825] border border-[#3f4816]/40 rounded-xl p-5">
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#888680] mb-1.5">Platform</label>
              <select
                required
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="w-full bg-[#1a1c1b] border border-[#3f4816]/50 rounded-lg px-3 py-2 text-[#dfddd6] text-sm focus:border-[#d9fb06] outline-none"
                data-testid="embed-platform-select"
              >
                <option value="">Select platform…</option>
                <option value="Instagram">Instagram</option>
                <option value="Facebook">Facebook</option>
                <option value="TikTok">TikTok</option>
                <option value="YouTube">YouTube</option>
                <option value="X (Twitter)">X (Twitter)</option>
                <option value="LinkedIn">LinkedIn</option>
                <option value="Snapchat">Snapchat</option>
                <option value="Reddit">Reddit</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#888680] mb-1.5">Account type</label>
              <select
                required
                value={accountType}
                onChange={(e) => setAccountType(e.target.value)}
                className="w-full bg-[#1a1c1b] border border-[#3f4816]/50 rounded-lg px-3 py-2 text-[#dfddd6] text-sm focus:border-[#d9fb06] outline-none"
                data-testid="embed-account-type"
              >
                <option value="">Select…</option>
                <option value="Personal">Personal</option>
                <option value="Business">Business</option>
                <option value="Creator">Creator</option>
                <option value="Ad Account">Ad Account</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#888680] mb-1.5">
                What did the platform say? (paste the notice)
              </label>
              <textarea
                required
                rows={4}
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                placeholder="E.g. 'Your account has been disabled for violating our Community Guidelines...'"
                className="w-full bg-[#1a1c1b] border border-[#3f4816]/50 rounded-lg px-3 py-2 text-[#dfddd6] text-sm focus:border-[#d9fb06] outline-none resize-none"
                data-testid="embed-reason-text"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#d9fb06] text-[#1a1c1b] font-bold py-3 rounded-lg hover:bg-[#d9fb06]/90 transition disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="embed-analyze-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Analyze My Suspension"}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>
        ) : (
          <div className="bg-[#252825] border border-[#3f4816]/40 rounded-xl p-5 space-y-4" data-testid="embed-result">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-[#d9fb06] flex-shrink-0" />
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#888680]">Likely category</p>
                <h2 className="text-lg font-black text-[#dfddd6]">{result.label}</h2>
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#888680] mb-1">Severity</p>
              <p className="text-sm text-[#dfddd6] font-semibold capitalize">{result.severity}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#888680] mb-1">Summary</p>
              <p className="text-sm text-[#888680] leading-relaxed">{result.summary}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#888680] mb-1">Recommended next step</p>
              <p className="text-sm text-[#dfddd6] font-semibold">
                {TIER_INFO[result.recommendedTier]?.label || result.recommendedTier}
              </p>
            </div>
            <div className="pt-3 border-t border-[#3f4816]/30">
              <a
                href="https://socialdisputeco.com/analyze"
                target="_top"
                rel="noopener"
                className="inline-flex items-center gap-2 text-[#d9fb06] hover:underline text-sm font-bold"
                data-testid="embed-full-analyzer-link"
              >
                Get the full analysis + appeal guide
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        )}

        {/* Attribution footer — the whole reason this widget exists */}
        <div className="mt-4 text-center">
          <a
            href="https://socialdisputeco.com/"
            target="_top"
            rel="noopener"
            className="text-[10px] uppercase tracking-widest text-[#888680] hover:text-[#d9fb06] font-mono"
          >
            Powered by <span className="text-[#d9fb06]">Social Dispute Co</span> · Independent appeal documentation
          </a>
        </div>
      </div>
    </div>
  );
}
